import React, { useMemo } from "react";
import { useContext } from "react";
import SharedStateContext from "../../contexts/shared-state/SharedStateContext";
import { red } from "@material-ui/core/colors";
import JSZip from "jszip";
import { CdrService } from "../../services/cdr/CdrService";
import { saveAs } from "file-saver";
import { handleError } from "../../utils/ErrorHandlers";
import { convertTZToLocalCDR } from "../general/DateTzHelper";




export default function useCdrExport() {
    const context = useContext(SharedStateContext);
    const cdrService = useMemo(() => new CdrService, []);

    function redefinedData(data, headers) {
        let dataArray = [];
        dataArray.push(headers);
        data.forEach((item) => {
            dataArray.push(Object.values(item));
        });
        return dataArray;
    }

    function formatDataToExport(selectedRows) {
        let dataArray = [];
        Object.values(selectedRows).forEach((data) => {
            let rowObject = {};
            rowObject.cdrId = data.cdrId;
            rowObject.a = data.a;
            rowObject.customerName =
                data.customerName == "---" ? null : data.customerName;
            rowObject.b = data.b;
            rowObject.callRecord =
                data.isSwitched == "true" && data.callResult == "ANSWERED_BY_AGENT"
                    ? data.cdrId
                    : null;
            rowObject.voicemail = data.hasVoiceMail ? data.cdrId : null;
            rowObject.startTime = data.startTime;
            rowObject.endTime = data.endTime;
            rowObject.switchTime = data.switchTime == "---" ? null : data.switchTime;
            rowObject.callResult = data.callResult;
            rowObject.callDuration = data.callDuration;
            rowObject.callDirection = data.callDirection;

            dataArray.push(rowObject);
        });
        return dataArray;
    }

    function formatSwitchDataToExport(selectedRows) {
        let dataArray = [];

        Object.values(selectedRows).forEach((data) => {
            let rowObject = {};
            rowObject.cdrId = data.cdrId;
            rowObject.username = data.username;
            rowObject.switchTime = data.switchTime == "---" ? null : data.switchTime;
            rowObject.switchingStatus = data.switchingStatus;
            rowObject.callStatus = data.callStatus;
            rowObject.answerTime = data.answerTime;
            rowObject.endTime = data.endTime;
            dataArray.push(rowObject);
        });

        return dataArray;
    }

    function createCsvFile(data) {
        let csvContent = "";

        data.forEach((row) => {
            csvContent += row.join(",") + "\n";
        });

        const cdrBlob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8,",
        });
        return cdrBlob;
    }

    function createCsvFileDataNoSwitched(data, name) {
        let csvContent = "";
        data.forEach((row) => {
            csvContent += row.join(",") + "\n";
        });

        const cdrBlob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8,",
        });
        const Url = URL.createObjectURL(cdrBlob);
        var a = window.document.createElement("a");
        a.href = Url;
        a.download = name;

        // Append anchor to body  .
        document.body.appendChild(a);
        a.click();

        // Remove anchor from body
        document.body.removeChild(a);
    }

    const createSelectedCdrZipFolder = async (data, name, idsList, cdrSwitchedHeaders, callRecordsDownloadable, voiceMailsDownloadable) => {
        const zip = new JSZip();
        if (idsList.length != 0) {
            let switchedData = await getSelectedSwitchedCRD(idsList);
            let redefinedSwitchedData = redefinedData(
                formatSwitchDataToExport(switchedData),
                cdrSwitchedHeaders
            );

            zip.file("Switched.csv", createCsvFile(redefinedSwitchedData));
        }
        let callRecordsFiles = await downloadCdrFile(
            callRecordsDownloadable
        );
        let voicemailsFiles = await downloadCdrVoicemailFile(
            voiceMailsDownloadable
        );

        zip.file("CDR.csv", createCsvFile(data));
        zip.file("callRecords.zip", callRecordsFiles);
        zip.file("voicemails.zip", voicemailsFiles);
        zip.generateAsync({ type: "blob" }).then(function (content) {
            saveAs(content, name);
        });
    };

    const createAllCdrZipFolder = async (data, name, from, to, cdrSwitchedHeaders, isExpired, callRecordsFlag, voiceMailsFlag) => {
        const zip = new JSZip();
        let switchedData = await getAllSwitchedCRD(
            from,
            to
        );

        let redefinedSwitchedData = redefinedData(
            formatSwitchDataToExport(switchedData),
            cdrSwitchedHeaders
        );
        let callRecordsDownloadable = [];
        let voiceMailsDownloadable = [];
        for (let i = 1; i < data.length; i++) { // skip column headers
            if (data[i][4] !== null) {
                if (callRecordsFlag) {
                    if (!isExpired((data[i][6]))) {
                        callRecordsDownloadable.push(data[i][4]);
                    }
                } else {
                    callRecordsDownloadable.push(data[i][4]);
                }
            }
            if (data[i][5] !== null) {
                if (voiceMailsFlag) {
                    if (!isExpired((data[i][6]))) {
                        voiceMailsDownloadable.push(data[i][5]);
                    }
                } else {
                    voiceMailsDownloadable.push(data[i][5]);
                }
            }

        }

        const [callRecordsFiles, voicemailsFiles] = await Promise.all([
            downloadCdrFile(callRecordsDownloadable),
            downloadCdrVoicemailFile(voiceMailsDownloadable)
        ]);

        zip.file("CDR.csv", createCsvFile(data));
        zip.file("Switched.csv", createCsvFile(redefinedSwitchedData));
        zip.file("callRecords.zip", callRecordsFiles);
        zip.file("voicemails.zip", voicemailsFiles);

        zip.generateAsync({ type: "blob" }).then(function (content) {
            saveAs(content, name);
        });
    };

    async function getAllSwitchedCRD(from, to) {
        try {

            let data = await cdrService.getAllSwitchedCRDDateService(
                context.session_id,
                from,
                to
            );
            return data.ivrList;
        } catch (e) {
            handleError(e);
        }
    }

    async function getSelectedSwitchedCRD(ids) {
        try {
            // setIsExporting(true);
            let data = await cdrService.getSelectedSwitchedCRDDateService(
                context.session_id,
                ids
            );

            // setIsExporting(false);
            return data.ivrList;
        } catch (e) {
            handleError(e);
        }
    }

    async function downloadCdrFile(ids) {
        try {
            // setIsExporting(true);
            let data = await cdrService.downloadCdrFiles(
                context.session_id,
                ids
            );
            // setIsExporting(false);
            return data;
        } catch (e) {
            handleError(e);
        }
    }

    async function downloadCdrVoicemailFile(ids) {
        try {
            // setIsExporting(true);
            let data = await cdrService.downloadCdrVoicemailFiles(
                context.session_id,
                ids
            );

            // setIsExporting(false);
            return data;
        } catch (e) {
            handleError(e);
        }
    }

    return [redefinedData, formatDataToExport, formatSwitchDataToExport,
        createCsvFile, createCsvFileDataNoSwitched,
        createSelectedCdrZipFolder, createAllCdrZipFolder];

}